/**
 * Direct FlatBuffer fetch+decode for a single adapter, bypassing RTK Query.
 *
 * RTK Query's lazy query trigger replaces its subscription on each call,
 * so concurrent `trigger()` calls in `Promise.all` silently drop all but
 * the last adapter's result.  Even `dispatch(initiate)` resolves with
 * undefined for certain adapters.  This direct fetch is the reliable path
 * for concurrent adapter fetches in the setup wizard.
 *
 * Because it bypasses RTK Query it also bypasses RTK Query's `retry`
 * middleware. The setup wizard fires a `Promise.all` of these (one per
 * bank-referenced adapter) during its initial load; under that burst an
 * individual request can transiently reject with `TypeError: Failed to fetch`
 * (connection dropped/refused under load) — observed deterministically on one
 * adapter of a concurrent batch while an immediate re-request succeeds. A
 * transient blip must NOT degrade the Added-Adapters row to a blank/Pending
 * fallback, so the transport (fetch + body read) is retried a few times with a
 * short backoff. HTTP error statuses and decode failures are NOT retried —
 * neither is transient.
 */
import { extractEnvelopeWithHash } from "../envelope";
import { getSchemaHandler } from "./schemaRegistry";

const MAX_TRANSPORT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 150;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Fetch the raw adapter bytes with transient-failure retry. Returns null for a
 * definitive HTTP error (non-2xx), the response bytes on success, or throws the
 * last network error once the retries are exhausted. Written recursively rather
 * than as an await-in-loop: retries are inherently sequential (each must wait
 * for the prior), so there is nothing to parallelize.
 */
async function fetchAdapterBytes(
  url: string,
  headers: Record<string, string>,
  attempt = 0,
): Promise<Uint8Array | null> {
  try {
    const resp = await fetch(url, { headers });
    if (!resp.ok) return null;
    return new Uint8Array(await resp.arrayBuffer());
  } catch (err) {
    if (attempt >= MAX_TRANSPORT_RETRIES) throw err;
    await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
    return fetchAdapterBytes(url, headers, attempt + 1);
  }
}

export default async function fetchAdapterDirect(
  id: string,
  token: string,
): Promise<Record<string, unknown> | null> {
  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "";
  const bytes = await fetchAdapterBytes(`${baseUrl}/adapters/${id}`, {
    authorization: token,
    Accept: "application/flatbuffers",
  });
  if (!bytes) return null;
  const envelope = extractEnvelopeWithHash(bytes);
  if (!envelope) return null;
  const handler = getSchemaHandler(envelope.schemaHash);
  if (!handler) return null;
  handler.verify(envelope.payload);
  const unpacked = handler.unpack(envelope.payload) as Record<string, unknown> | null;
  return (unpacked?.data ?? unpacked) as Record<string, unknown> | null;
}
